import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Select from 'antd/lib/select';
import Icon from 'antd/lib/icon';
import Button from 'antd/lib/button';
import Table from 'antd/lib/table';
import { translate } from 'react-i18next';
import { Row, Col } from 'react-flexbox-grid';
import obligationType, { translation as obligationTypeTranslation } from '../../../../common/enums/obligationType';

const { Option } = Select;

const CARD_LIKE_TYPES = [obligationType.KREDITNA_KARTA, obligationType.KONTOKORENT];

const isCardLikeType = type => CARD_LIKE_TYPES.indexOf(type) !== -1;

// Obligations are a snapshot taken with each loan application; only the newest
// application's set is the live one, so editing older ones would rewrite history.
// Newest first, so the one editable block sits above the past applications
// instead of below all of them.
const newestFirst = loans =>
  (loans || []).slice().sort((a, b) => {
    const diff = new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    return diff !== 0 ? diff : String(b.loanNumber).localeCompare(String(a.loanNumber));
  });

// Renders nothing; only carries the aggregated form value/validation
// (class component so rc-form can attach its ref).
class HiddenField extends Component {
  render() {
    return null;
  }
}

export class ObligationsHelper extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    account: PropTypes.object.isRequired,
    isEditable: PropTypes.bool.isRequired,
    isCallCentrum: PropTypes.bool.isRequired,
    form: PropTypes.object.isRequired,
  };

  keySeq = 0;

  constructor(props) {
    super(props);
    this.state = {
      data: this.buildInitialData(props.account),
    };
  }

  componentDidUpdate(prevProps) {
    const { account, isEditable } = this.props;
    // Rebuilding on every new account object would discard the admin's
    // unsaved rows whenever an unrelated background refetch lands mid-edit.
    const isDifferentAccount = (prevProps.account || {}).id !== (account || {}).id;
    if (isDifferentAccount || (!prevProps.isEditable && isEditable)) {
      const data = this.buildInitialData(account);
      // The form field only exists in edit mode; syncing it in view mode
      // would trigger rc-form's "field not registered" warning.
      this.setState({ data }, () => this.isEditMode() && this.syncFormValue(data));
    }
  }

  isEditMode = () => this.props.isEditable && !this.props.isCallCentrum;

  nextKey = () => {
    this.keySeq += 1;
    return `new-${this.keySeq}`;
  };

  buildInitialData = account =>
    newestFirst((account || {}).Loans).map((loan, index) => ({
      loanId: loan.id,
      loanNumber: loan.loanNumber,
      isEditable: index === 0,
      rows: (loan.Obligations || []).map(obligation => ({
        key: obligation.id != null ? `id-${obligation.id}` : this.nextKey(),
        type: obligation.type,
        originalAmount: obligation.originalAmount,
        monthlyPayment: obligation.monthlyPayment,
        provider: obligation.provider,
      })),
    }));

  // Past applications are displayed but never submitted, so a save cannot touch them.
  toContractValue = data =>
    data.filter(loan => loan.isEditable).map(loan => ({
      loanId: loan.loanId,
      obligations: loan.rows.map(row => ({
        type: row.type,
        originalAmount: row.originalAmount,
        monthlyPayment: isCardLikeType(row.type) ? null : row.monthlyPayment,
        provider: row.provider,
      })),
    }));

  syncFormValue = data => {
    this.props.form.setFieldsValue({ loanObligations: this.toContractValue(data) });
  };

  updateData = updater => {
    this.setState(
      prevState => ({ data: updater(prevState.data) }),
      () => this.syncFormValue(this.state.data)
    );
  };

  addObligation = loanId => {
    const key = this.nextKey();
    this.updateData(data =>
      data.map(loan =>
        loan.loanId === loanId
          ? { ...loan, rows: [...loan.rows, { key, type: undefined, originalAmount: undefined, monthlyPayment: undefined, provider: '' }] }
          : loan
      )
    );
  };

  removeObligation = (loanId, key) => {
    this.updateData(data =>
      data.map(loan =>
        loan.loanId === loanId ? { ...loan, rows: loan.rows.filter(row => row.key !== key) } : loan
      )
    );
  };

  updateObligation = (loanId, key, patch) => {
    this.updateData(data =>
      data.map(loan => {
        if (loan.loanId !== loanId) {
          return loan;
        }
        return {
          ...loan,
          rows: loan.rows.map(row => {
            if (row.key !== key) {
              return row;
            }
            const nextRow = { ...row, ...patch };
            if (isCardLikeType(nextRow.type)) {
              nextRow.monthlyPayment = null;
            }
            return nextRow;
          }),
        };
      })
    );
  };

  validateObligations = (rule, value, callback) => {
    const { t } = this.props;
    if (!Array.isArray(value)) {
      callback();
      return;
    }

    const isPositiveInt = n => Number.isInteger(n) && n > 0;

    for (const loan of value) {
      for (const obligation of loan.obligations || []) {
        if (!obligation.type) {
          callback(t('Vyberte typ záväzku'));
          return;
        }
        if (!obligation.provider || obligation.provider.length > 20) {
          callback(t('Zadajte kto poskytol (max. 20 znakov)'));
          return;
        }
        if (!isPositiveInt(obligation.originalAmount)) {
          callback(t('Zadajte platnú pôvodnú výšku'));
          return;
        }
        if (!isCardLikeType(obligation.type) && !isPositiveInt(obligation.monthlyPayment)) {
          callback(t('Zadajte platnú mesačnú splátku'));
          return;
        }
      }
    }

    callback();
  };

  renderView() {
    const { account, t } = this.props;
    const dataSource = (account.Loans || []).flatMap(loan =>
      (loan.Obligations || []).map(obligation => ({ ...obligation, loanNumber: loan.loanNumber }))
    );

    if (dataSource.length === 0) {
      return null;
    }

    return (
      <Table
        dataSource={dataSource}
        columns={[
          { title: t('Pôžička'), dataIndex: 'loanNumber' },
          { title: t('Typ záväzku'), dataIndex: 'type', render: v => obligationTypeTranslation[v] || v },
          { title: t('Pôvodná výška v EUR'), dataIndex: 'originalAmount' },
          { title: t('Mesačná splátka v EUR'), dataIndex: 'monthlyPayment', render: v => (v != null ? v : '-') },
          { title: t('Kto poskytol'), dataIndex: 'provider' },
        ]}
        pagination={false}
        size="small"
        rowKey="id"
      />
    );
  }

  // Past applications stay on screen so nothing appears to vanish mid-edit,
  // but they carry no inputs and no add button.
  renderReadOnlyRows(loan) {
    return loan.rows.map(row => (
      <Row key={row.key} style={{ marginTop: 6, alignItems: 'center' }}>
        <Col xs={3} sm={3} md={3} lg={3}>{obligationTypeTranslation[row.type] || row.type}</Col>
        <Col xs={2} sm={2} md={2} lg={2}>{row.originalAmount}</Col>
        <Col xs={2} sm={2} md={2} lg={2}>{row.monthlyPayment != null ? row.monthlyPayment : '-'}</Col>
        <Col xs={3} sm={3} md={3} lg={3}>{row.provider}</Col>
      </Row>
    ));
  }

  renderEditableLoan(loan) {
    const { t } = this.props;

    return (
      <React.Fragment>
        {loan.rows.map(row => (
          <Row key={row.key} style={{ marginTop: 6, alignItems: 'center' }}>
            <Col xs={3} sm={3} md={3} lg={3}>
              <Select
                style={{ width: '100%' }}
                placeholder={t('Typ záväzku')}
                value={row.type}
                onChange={value => this.updateObligation(loan.loanId, row.key, { type: value })}
              >
                {Object.values(obligationType).map(type => (
                  <Option key={type} value={type}>
                    {obligationTypeTranslation[type]}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={2} sm={2} md={2} lg={2}>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                precision={0}
                placeholder={t('Pôvodná výška v EUR')}
                value={row.originalAmount}
                onChange={value => this.updateObligation(loan.loanId, row.key, { originalAmount: value })}
              />
            </Col>
            <Col xs={2} sm={2} md={2} lg={2}>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                precision={0}
                placeholder={t('Mesačná splátka v EUR')}
                disabled={isCardLikeType(row.type)}
                value={isCardLikeType(row.type) ? null : row.monthlyPayment}
                onChange={value => this.updateObligation(loan.loanId, row.key, { monthlyPayment: value })}
              />
            </Col>
            <Col xs={3} sm={3} md={3} lg={3}>
              <Input
                maxLength="20"
                placeholder={t('Kto poskytol')}
                value={row.provider}
                onChange={e => this.updateObligation(loan.loanId, row.key, { provider: e.target.value })}
              />
            </Col>
            <Col xs={1} sm={1} md={1} lg={1}>
              <Icon
                className="dynamic-delete-button"
                type="minus-circle-o"
                title={t('Odstrániť záväzok')}
                onClick={() => this.removeObligation(loan.loanId, row.key)}
              />
            </Col>
          </Row>
        ))}
        <Row style={{ marginTop: 6 }}>
          <Col xs={11} sm={11} md={11} lg={11}>
            <Button type="dashed" size="small" onClick={() => this.addObligation(loan.loanId)} style={{ width: '100%' }}>
              <Icon type="plus" /> {t('Pridať záväzok')}
            </Button>
          </Col>
        </Row>
      </React.Fragment>
    );
  }

  renderEdit() {
    const { t, form } = this.props;
    const { getFieldDecorator } = form;
    const { data } = this.state;

    return (
      <React.Fragment>
        {data.map(loan => (
          <div key={loan.loanId} style={{ marginBottom: 12 }}>
            <strong>{`${t('Pôžička č.')} ${loan.loanNumber}`}</strong>
            {loan.isEditable ? this.renderEditableLoan(loan) : this.renderReadOnlyRows(loan)}
          </div>
        ))}
        <Form.Item>
          {getFieldDecorator('loanObligations', {
            initialValue: this.toContractValue(data),
            rules: [{ validator: this.validateObligations }],
          })(<HiddenField />)}
        </Form.Item>
      </React.Fragment>
    );
  }

  render() {
    return this.isEditMode() ? this.renderEdit() : this.renderView();
  }
}

export default translate()(ObligationsHelper);



// WEBPACK FOOTER //
// ./src/features/account-management/components/profile/ObligationsHelper.js